/**
 * โคมแดง, the red lantern event (docs/archive/QINGGONG_DROPS_RED_LANTERN.md Part B). Data only; rules: systems/redLantern.ts,
 * quests: data/redLanternQuests.ts. A red paper lantern drifts near the player now and then; touching it in the air
 * opens it: a gift, or a note (ใบโคม) naming an NPC who needs help. Every roll is one draw of the World's Rng.
 */
import type { ZoneId } from './zones';

export const RED_LANTERN = {
  zones: ['yanyu2'] as readonly ZoneId[],
  /** Seconds of eligible time between rolls, and the chance a roll brings a lantern. */
  rollEvery: 60,
  chance: 0.04,
  /** The chance is multiplied in the world-event cycle's quiet minutes, and in the daily event `lanterns` (คืนโคมแดง). */
  quietMul: 2,
  nightMul: 3,
  nightEvent: 'lanterns',
  /** Eligible seconds with no lantern after which the next roll always brings one. */
  guard: 1800,
  /** Lanterns a day (`progress.arena.day`). */
  perDay: 8,
  /** Quest done once (index of the quest line). */
  minQuest: 1,
  /** Spawn ring round the player (m), height above the ground (m), how long it hangs (s). */
  spawnMin: 18,
  spawnMax: 30,
  hMin: 3,
  hMax: 8,
  life: 90,
  /** Touching: flat reach and height band (m); the body must be off the floor. */
  reach: 2.5,
  band: 2.5,
  /** The NPC rival of the pvp mode: starts this far away (m), runs at this share of the player's sprint. */
  rival: { from: 40, speedMul: 0.85, sprint: 1.8 },
} as const;

export type LanternGift = 'purse' | 'goods' | 'gear' | 'gem' | 'page';
export type LanternQuestKind = 'deliver' | 'find' | 'help' | 'escort';

/** The reward table: weights. The quests drop to 0 while one is open or no giver stands in the zone. */
export const LANTERN_TABLE: readonly { readonly kind: LanternGift | LanternQuestKind; readonly w: number }[] = [
  { kind: 'purse', w: 18 },
  { kind: 'goods', w: 14 },
  { kind: 'gear', w: 10 },
  { kind: 'gem', w: 8 },
  { kind: 'page', w: 3 },
  { kind: 'deliver', w: 14 },
  { kind: 'find', w: 12 },
  { kind: 'help', w: 12 },
  { kind: 'escort', w: 9 },
];

export const LANTERN_GIFTS = {
  /** A purse: min-max ตำลึง times (1 + level / 10). */
  purse: [20, 60] as const,
  /** Goods (data/professions.ts) and how many (min-max). */
  goods: { ids: ['rice', 'herb-leaf', 'hard-wood', 'carp', 'charcoal'] as readonly string[], n: [2, 4] as const },
  /** Gear: shop pieces (data/shop.ts item ids are taken from there), one drawn. */
  /** A gem (good) 6 : a reroll stone 2. */
  gems: [
    { good: 'gem-pearl-1', w: 6 },
    { good: 'reroll-stone', w: 2 },
  ] as readonly { readonly good: string; readonly w: number }[],
  /** A page (หน้าตำรา) of a drop-source art the player has not learned: `qgPageId(art)` over `QG_ARTS` (data/qinggongDrops.ts). */
} as const;
